import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { SITE_NAME, SITE_TAGLINE } from "../lib/siteBranding";
import { SiteNameWordmark } from "./SiteNameWordmark";

type SiteInfoModalProps = {
  open: boolean;
  onClose: () => void;
};

export function SiteInfoModal({ open, onClose }: SiteInfoModalProps) {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-end justify-center p-4 sm:items-center sm:p-6"
      role="presentation"
    >
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-[2px]"
        aria-label="Close dialog"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative z-10 w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-lg font-semibold tracking-tight text-slate-900">
              About {SITE_NAME}
            </h2>
            <p className="mt-1 text-xs leading-snug text-slate-500">{SITE_TAGLINE}</p>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="shrink-0 rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            aria-label="Close"
            onClick={onClose}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate-600">
          <p>
            <SiteNameWordmark className="text-sm" /> currently shows{" "}
            <strong className="font-semibold text-slate-800">software and tech</strong> job postings
            only, and only for roles based in{" "}
            <strong className="font-semibold text-slate-800">California</strong>.
          </p>
          <p>
            All job postings you see here are from the <strong className="font-semibold text-slate-800">last seven days</strong>.
          </p>
          <p>
            Listings older than seven days are <strong className="font-semibold text-slate-800">removed automatically</strong>, so only the latest openings stay on the site.
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
